import { Download, Eye, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FileTypeIcon } from "@/components/ui/documents";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatFileSize } from "@/utils/formatFileSize";
import { useFileDownload } from "../useFileDownload";
import { useSigningPreview } from "../signingPreviewContext";
import type { SigningFile } from "../types";

interface SigningFileListProps {
  title: string;
  files: SigningFile[];
  isLoading: boolean;
  /** Query failure — shown instead of the list, so an RLS/network error never
   *  masquerades as an empty container. */
  isError: boolean;
}

/** The files a container holds, or the documents a session will sign. */
export function SigningFileList({ title, files, isLoading, isError }: SigningFileListProps) {
  const { t } = useLanguage();
  const download = useFileDownload();
  const preview = useSigningPreview();

  return (
    <section>
      <h3 className="mb-2 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
      ) : isError ? (
        <p className="text-[11px] text-destructive">{t.signing.panel.contentsLoadFailed}</p>
      ) : files.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t.signing.panel.noFiles}</p>
      ) : (
        <ul className="flex flex-col">
          {files.map((file) => {
            // Opened in the shared document viewer, as on the Documents page.
            const open = preview?.canOpenFile(file) ? () => preview.openFile(file) : null;
            return (
              <li key={file.id} className="flex items-center gap-3 py-2">
                <FileTypeIcon filename={file.filename} />
                <div className="min-w-0 flex-1">
                  {open ? (
                    <button
                      type="button"
                      onClick={open}
                      className="block max-w-full truncate text-left text-xs font-medium hover:text-primary hover:underline"
                    >
                      {file.filename}
                    </button>
                  ) : (
                    <div className="truncate text-xs font-medium">{file.filename}</div>
                  )}
                  <div className="truncate text-[11px] text-muted-foreground">
                    {[file.uploadedBy, formatFileSize(file.size)].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {open && (
                  <Button
                    variant="ghost"
                    size="controlIcon"
                    aria-label={`${t.documents.signedContainer.panel.open} ${file.filename}`}
                    disabled={preview?.openingId === file.id}
                    onClick={open}
                  >
                    {preview?.openingId === file.id ? (
                      <Loader2 className="animate-spin" aria-hidden="true" />
                    ) : (
                      <Eye aria-hidden="true" />
                    )}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="controlIcon"
                  disabled={!file.filePath}
                  aria-label={`${t.signing.panel.download} ${file.filename}`}
                  onClick={() => download(file.filePath, file.filename)}
                >
                  <Download aria-hidden="true" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
